import {
  type ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { MessageSquareIcon, Repeat2Icon, SparklesIcon } from "lucide-react"

import {
  Button,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { ScrollToBottomButton } from "@f2e/ui/components/Button"
import {
  useChatSessionV2,
  type ConversationPartApiResponse,
  type ImageEditRegion,
} from "@f2e/ui/chat-v2"
import {
  ImageEditDialog,
  getConversationPartText,
  parsePaymentSummaryContent,
  QuestionPanel,
  type FeedbackBlockedReason,
  type ImageEditTarget,
} from "@f2e/ui/components/Chat"
import { useScrollToBottom } from "@f2e/ui/hooks"

import {
  PaymentRequiredDialog,
  PendingInteractionPaymentCard,
  WebConversationPartPanel,
  ActionType,
} from "@/components"
import { toUIHistory } from "@/lib/chatMapper"
import {
  useToast,
  useSysConfig,
  useFileUpload,
  ConversationErrorType,
  AskStatus,
} from "@/hooks"
import { BILLING_OVERVIEW_REFRESH_EVENT, SETTINGS_ENDPOINTS } from "@/constants"
import { isOrgBudgetErrorCode, SOMETHING_WRONG_MSG } from "@/constants/errors"
import {
  Agent,
  AgentHistory,
  ConversationContext,
  ConversationSettings,
  ConversationType,
  History,
  Workspace,
} from "@/types"
import {
  downloadAttachmentResource,
  loadAttachmentPreviewResource,
  resolveAttachmentResourceUrl,
} from "@/lib/attachmentResource"
import { useStore } from "@/store"
import {
  getModelDisplayName,
  getSystemDefaultTextModelId,
  getVoiceRecordingDisabledReason,
  validateAudioModel,
} from "@/lib/modelCapabilities"
import { redirectToLoginForExpiredSession } from "@/lib/authRedirect"
import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"
import { OnboardingEmptyState } from "@/components/System"

import { usePlaygroundChatContext } from "./types"

interface ThreadPanelProps {
  agentHistoryId?: string
  agent?: Agent
  workspace?: Workspace
  agentHistories?: AgentHistory[]
  variant?: "default" | "copilot"
  showFirstTestGuidance?: boolean
  showCopilotIntroduction?: boolean
  showVersionTestGuidance?: boolean
  versionTestQuestion?: string
  versionTestVersion?: number
  onProcessingChange?: (isProcessing: boolean) => void
  onFirstSuccessfulResponse?: () => void
  onOpenCopilot?: () => void
  onDismissCopilotIntroduction?: () => void
  onRetryPreviousQuestion?: () => void
  onLatestUserQuestionChange?: (question?: string) => void
  onActiveHistoryChange?: (history?: History) => void
  onHistoryTitleUpdated?: (historyId: number, name: string) => void
  onConversationSettled?: () => void
  onCreateEvalPart?: (
    questionPart: ConversationPartApiResponse,
    answerPart: ConversationPartApiResponse,
    allParts: ConversationPartApiResponse[]
  ) => void
}

export const getAgentHistoryId = (metadata: Record<string, unknown>) => {
  const value = metadata.agent_history_id ?? metadata.agentHistoryId
  return typeof value === "string" && value.trim() ? value : undefined
}

export const getAgentVersion = (
  metadata: Record<string, unknown>,
  agentHistories: AgentHistory[]
) => {
  const directVersion = metadata.agent_version ?? metadata.agentVersion
  if (typeof directVersion === "number" && Number.isFinite(directVersion)) {
    return directVersion
  }
  const historyId = getAgentHistoryId(metadata)
  return agentHistories.find((history) => history.id === historyId)
    ?.versionNumber
}

export const agentHistorySupportsImageRegionEditing = (
  agentHistoryId: string | undefined,
  agentHistories: AgentHistory[]
) =>
  agentHistories.find((history) => history.id === agentHistoryId)
    ?.supportsImageRegionEditing ?? false

export const isCommittedHistoryLoad = (
  loadedParts: ConversationPartApiResponse[] | undefined,
  requestedScope: string,
  activeScope: string
) => loadedParts !== undefined && requestedScope === activeScope

export const hasCompletedAssistantResponse = (
  parts: ConversationPartApiResponse[],
  isProcessing: boolean
) =>
  !isProcessing &&
  parts.some(
    (part) =>
      part.part_kind === "text" &&
      getConversationPartText(part).trim().length > 0
  )

export const useFirstLiveTestCompletion = ({
  scope,
  enabled,
  parts,
  isProcessing,
  onComplete,
}: {
  scope?: string
  enabled: boolean
  parts: ConversationPartApiResponse[]
  isProcessing: boolean
  onComplete?: () => void
}) => {
  const observedScopeRef = useRef<string | undefined>(undefined)
  const armedScopeRef = useRef<string | undefined>(undefined)
  const notifiedScopesRef = useRef(new Set<string>())
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  useEffect(() => {
    if (!enabled || !scope) {
      observedScopeRef.current = undefined
      armedScopeRef.current = undefined
      return
    }

    if (observedScopeRef.current !== scope) {
      observedScopeRef.current = scope
      // A newly selected Agent can render once with the previous Agent's
      // transcript. Only arm immediately when the new scope is already empty.
      armedScopeRef.current = parts.length === 0 ? scope : undefined
      return
    }

    if (parts.length === 0) {
      armedScopeRef.current = scope
      return
    }

    if (
      armedScopeRef.current !== scope ||
      notifiedScopesRef.current.has(scope) ||
      !hasCompletedAssistantResponse(parts, isProcessing)
    ) {
      return
    }

    notifiedScopesRef.current.add(scope)
    onCompleteRef.current?.()
  }, [enabled, isProcessing, parts, scope])
}

export const isThreadSwitchBlocked = ({
  isAsking,
  isHitlPending,
  hasSubmittingFormRequest,
  isLoadingHistory,
  isSubmittingImageEdit,
}: {
  isAsking: boolean
  isHitlPending: boolean
  hasSubmittingFormRequest: boolean
  isLoadingHistory: boolean
  isSubmittingImageEdit: boolean
}) =>
  isAsking ||
  isHitlPending ||
  hasSubmittingFormRequest ||
  isLoadingHistory ||
  isSubmittingImageEdit

export interface ThreadPanelHandle {
  hasUnsentContent: () => boolean
  isSwitchBlocked: () => boolean
  loadHistory: (history: History) => Promise<boolean>
  startNewTest: () => void
  retryQuestion: (question: string) => void
}

export const getLatestUserQuestion = (
  parts: ConversationPartApiResponse[]
): string | undefined => {
  const questionPart = [...parts]
    .reverse()
    .find((part) => part.part_kind === "user-prompt")
  const question = questionPart
    ? getConversationPartText(questionPart).trim()
    : ""
  return question || undefined
}

export const createAgentTestConversationContext = ({
  workspace,
  agent,
  agentHistoryId,
  question,
}: {
  workspace: Workspace
  agent: Agent
  agentHistoryId?: string
  question: string
}): ConversationContext => ({
  type: ConversationType.Conversation,
  workspace,
  question,
  files: [],
  settings: { agent, agentHistoryId },
})

export const useNotifyActiveHistoryChange = (
  currentHistory: History | undefined,
  onActiveHistoryChange?: (history?: History) => void
) => {
  const onActiveHistoryChangeRef = useRef(onActiveHistoryChange)
  onActiveHistoryChangeRef.current = onActiveHistoryChange

  useEffect(() => {
    onActiveHistoryChangeRef.current?.(
      currentHistory && currentHistory.id > 0 ? currentHistory : undefined
    )
  }, [currentHistory])
}

const ThreadPanelComponent = (
  {
    agentHistoryId,
    agent,
    workspace,
    agentHistories = [],
    variant = "default",
    showFirstTestGuidance = false,
    showCopilotIntroduction = false,
    showVersionTestGuidance = false,
    versionTestQuestion,
    versionTestVersion,
    onProcessingChange,
    onFirstSuccessfulResponse,
    onOpenCopilot,
    onDismissCopilotIntroduction,
    onRetryPreviousQuestion,
    onLatestUserQuestionChange,
    onActiveHistoryChange,
    onHistoryTitleUpdated,
    onConversationSettled,
    onCreateEvalPart,
  }: ThreadPanelProps,
  ref: ForwardedRef<ThreadPanelHandle>
) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { billingUiEnabled } = useSysConfig()
  const { alertToast, successToast } = useToast()
  const [userMe, availableModels, sysConfig] = useStore((state) => [
    state.userMe,
    state.availableModels,
    state.sysConfig,
  ])
  const [showPaymentRequired, setShowPaymentRequired] = useState(false)

  // Error handler for conversation errors (defined early for useChatSessionV2)
  const handleConversationError = useCallback(
    (errorType: ConversationErrorType, rawMessage?: string) => {
      console.error("handleConversationError", errorType, rawMessage)

      const match = rawMessage?.match(/^\[code=(\d+)\]\s*(.*)$/)
      const code = match ? Number(match[1]) : null
      const message = match ? match[2] : rawMessage
      const isOrgBudgetError = isOrgBudgetErrorCode(code)

      if (errorType === ConversationErrorType.SSEError && isOrgBudgetError) {
        window.dispatchEvent(new Event(BILLING_OVERVIEW_REFRESH_EVENT))
        setShowPaymentRequired(true)
        return
      }

      if (isOrgBudgetError) {
        window.dispatchEvent(new Event(BILLING_OVERVIEW_REFRESH_EVENT))
        alertToast({
          title: t("billing.paymentRequired.title"),
          description: message || t("billing.paymentRequired.description"),
        })
        return
      }

      let title = t(SOMETHING_WRONG_MSG)
      let description: string | undefined = t("constants.errors.backToHome")

      switch (errorType) {
        case ConversationErrorType.RefreshConversationFailed:
          title = t("common.conversation.errors.refreshConversationFailed")
          description = message
          break
        case ConversationErrorType.SSEError:
          title = t("common.conversation.errors.sseError")
          description = message || t("constants.errors.backToHome")
          break
      }

      alertToast({
        title,
        description,
      })
    },
    [t, alertToast]
  )

  // conversation context for initial conversation
  const {
    conversationContext: defaultConversationContext,
    setConversationContext: setDefaultConversationContext,
  } = usePlaygroundChatContext()

  // // scroll to bottom of the playground
  // const { scrollToBottom } = useScrollToBottom({
  //   selector: "[data-scroll='playground']",
  // })

  // Unified chat session hook - combines conversation state and SSE streaming
  const {
    context: currentContext,
    history: currentHistory,
    askingStatus,
    isAsking,
    parts,
    initConversation,
    loadConversations,
    refreshConversations,
    changeSettings,
    submitFeedback,
    deleteConversationGroup,
    // Chat actions
    sendMessage,
    regenerate,
    cancel,
    // HITL
    pendingPayment,
    isHitlPending,
    submittingFormRequestId,
    submitFormInteraction,
    rejectFormInteraction,
    clearPendingInteraction,
  } = useChatSessionV2({
    getBaseUrl: () => `${sysConfig.serverRoot}/api/v1`,
    onError: handleConversationError,
    onUnauthorized: redirectToLoginForExpiredSession,
    onChatTitleUpdated: onHistoryTitleUpdated,
    onComplete: onConversationSettled,
  })

  const [question, setQuestion] = useState<string>("")
  const [imageEditTarget, setImageEditTarget] =
    useState<ImageEditTarget | null>(null)
  const [isSubmittingImageEdit, setIsSubmittingImageEdit] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const supportsImageRegionEditing = agentHistorySupportsImageRegionEditing(
    agentHistoryId,
    agentHistories
  )
  const initSentContextRef = useRef<ConversationContext | null>(null)
  const initAudioErrorContextRef = useRef<ConversationContext | null>(null)
  const attachmentWorkspace = currentHistory?.workspace
  const attachmentOrganizationId =
    attachmentWorkspace?.organizationId ||
    (attachmentWorkspace?.id
      ? userMe?.profile.workspaceOrganizationMap?.[attachmentWorkspace.id]
      : undefined)
  const activeHistoryScopeRef = useRef("")
  activeHistoryScopeRef.current = `${workspace?.id ?? ""}:${agent?.id ?? ""}`
  useNotifyActiveHistoryChange(
    currentHistory as History | undefined,
    onActiveHistoryChange
  )

  useFirstLiveTestCompletion({
    scope:
      workspace && agent ? `${workspace.id}:${agent.id.toString()}` : undefined,
    enabled: showFirstTestGuidance,
    parts,
    isProcessing: isAsking || isHitlPending,
    onComplete: onFirstSuccessfulResponse,
  })

  useEffect(() => {
    setImageEditTarget(null)
    setIsSubmittingImageEdit(false)
  }, [currentHistory?.id])

  const latestUserQuestion = getLatestUserQuestion(parts)
  useEffect(() => {
    onLatestUserQuestionChange?.(latestUserQuestion)
  }, [latestUserQuestion, onLatestUserQuestionChange])

  const resolveAttachmentUrl = useCallback(
    (url: string) =>
      resolveAttachmentResourceUrl(url, sysConfig.serverRoot, {
        organizationId: attachmentOrganizationId,
        workspaceId: attachmentWorkspace?.id,
      }),
    [attachmentOrganizationId, attachmentWorkspace?.id, sysConfig.serverRoot]
  )
  const handleAttachmentDownload = useCallback(
    (file: { url?: string; name: string }) =>
      downloadAttachmentResource(file, sysConfig.serverRoot),
    [sysConfig.serverRoot]
  )
  const handleAttachmentPreviewLoad = useCallback(
    (file: { url?: string; name: string }) =>
      loadAttachmentPreviewResource(file, sysConfig.serverRoot),
    [sysConfig.serverRoot]
  )
  const resolvedVoiceModelId =
    currentContext?.settings?.agent?.llmModel ||
    agent?.llmModel ||
    userMe?.profile?.preferredLLM ||
    getSystemDefaultTextModelId(sysConfig, availableModels)
  const voiceRecordingDisabledReason = getVoiceRecordingDisabledReason({
    resolvedModelId: resolvedVoiceModelId,
    availableModels,
    unavailableReason: t("common.audioInput.unavailableModel"),
    unsupportedReason: () => t("common.audioInput.unsupportedAgent"),
  })
  // File upload hook for QuestionPanel
  const {
    fileBadges,
    uploadedFileUuids,
    isUploading,
    handleFiles: handleFileUpload,
    removeFile,
    clearFiles,
  } = useFileUpload({
    workspace: currentHistory?.workspace as Workspace | undefined,
    allowAudio: !voiceRecordingDisabledReason,
    onUploadError: (error) => {
      alertToast({
        title: t("common.fileUpload.error.title"),
        description: error.fileName
          ? `${error.fileName}: ${error.message}`
          : error.message,
      })
    },
  })

  const startNewTest = useCallback(() => {
    if (!workspace || !agent) return
    initSentContextRef.current = null
    setQuestion("")
    setImageEditTarget(null)
    clearFiles()
    initConversation(
      createAgentTestConversationContext({
        workspace,
        agent,
        agentHistoryId,
        question: "",
      })
    )
  }, [agent, agentHistoryId, clearFiles, initConversation, workspace])

  const retryQuestion = useCallback(
    (nextQuestion: string) => {
      const trimmedQuestion = nextQuestion.trim()
      if (!workspace || !agent || !trimmedQuestion) return
      initSentContextRef.current = null
      setQuestion("")
      setImageEditTarget(null)
      clearFiles()
      initConversation(
        createAgentTestConversationContext({
          workspace,
          agent,
          agentHistoryId,
          question: trimmedQuestion,
        })
      )
    },
    [agent, agentHistoryId, clearFiles, initConversation, workspace]
  )

  useEffect(() => {
    startNewTest()
    // Agent version changes intentionally keep the active history. Only the
    // Agent/workspace identity starts a new local test session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.id, workspace?.id])

  useImperativeHandle(
    ref,
    () => ({
      hasUnsentContent: () =>
        Boolean(question.trim() || fileBadges.length > 0 || isUploading),
      isSwitchBlocked: () =>
        isThreadSwitchBlocked({
          isAsking,
          isHitlPending,
          hasSubmittingFormRequest: Boolean(submittingFormRequestId),
          isLoadingHistory,
          isSubmittingImageEdit,
        }),
      loadHistory: async (targetHistory) => {
        if (
          isThreadSwitchBlocked({
            isAsking,
            isHitlPending,
            hasSubmittingFormRequest: Boolean(submittingFormRequestId),
            isLoadingHistory,
            isSubmittingImageEdit,
          })
        ) {
          return false
        }
        setIsLoadingHistory(true)
        const requestedScope = activeHistoryScopeRef.current
        try {
          const loadedParts = await loadConversations(targetHistory)
          if (
            !isCommittedHistoryLoad(
              loadedParts,
              requestedScope,
              activeHistoryScopeRef.current
            )
          ) {
            return false
          }
          setQuestion("")
          setImageEditTarget(null)
          clearFiles()
          return true
        } catch {
          return false
        } finally {
          setIsLoadingHistory(false)
        }
      },
      startNewTest,
      retryQuestion,
    }),
    [
      clearFiles,
      fileBadges.length,
      isAsking,
      isHitlPending,
      isLoadingHistory,
      isSubmittingImageEdit,
      isUploading,
      loadConversations,
      question,
      retryQuestion,
      startNewTest,
      submittingFormRequestId,
    ]
  )
  const handleVoiceRecordingError = useCallback(
    (error: Error) => {
      alertToast({
        title: t("common.audioInput.recordingFailed"),
        description: error.message,
      })
    },
    [alertToast, t]
  )

  // Scroll to bottom hook - uses ref mode for reliable scroll container binding
  const { scrollContainerRef, showScrollToBottom, scrollToBottom } =
    useScrollToBottom({
      hasContent: parts.length > 0,
    })

  // flow for new conversation
  useEffect(() => {
    if (isAsking) return
    if (!defaultConversationContext) return

    initConversation(defaultConversationContext)

    // clear the conversation context
    setDefaultConversationContext(null)
  }, [
    defaultConversationContext,
    isAsking,
    initConversation,
    setDefaultConversationContext,
  ])

  // Update conversation settings when agentHistoryId or agent changes
  useEffect(() => {
    if (!currentContext || !currentContext.settings) {
      return
    }

    if (!agent || !agentHistoryId) {
      return
    }

    // Only update if the settings don't already have the correct agentHistoryId
    if (
      currentContext.settings.agentHistoryId === agentHistoryId &&
      currentContext.settings.agent?.id === agent.id
    ) {
      return
    }

    const updatedSettings: ConversationSettings = {
      ...currentContext.settings,
      agent,
      agentHistoryId,
    }

    changeSettings(updatedSettings)
  }, [agentHistoryId, agent, currentContext, changeSettings])

  // Notify parent when conversation processing state changes
  useEffect(() => {
    onProcessingChange?.(
      isAsking || isHitlPending || Boolean(submittingFormRequestId)
    )
  }, [isAsking, isHitlPending, onProcessingChange, submittingFormRequestId])

  // for the initial conversation, it only runs once when status becomes Init
  useEffect(() => {
    if (askingStatus !== AskStatus.Init) return
    if (!currentContext) return

    const files = currentContext.files || []
    if (!currentContext.question.trim() && files.length === 0) return
    const resolvedModelId =
      currentContext.settings?.agent?.llmModel ||
      agent?.llmModel ||
      userMe?.profile?.preferredLLM ||
      getSystemDefaultTextModelId(sysConfig, availableModels)
    const audioError = validateAudioModel(
      files,
      resolvedModelId,
      availableModels
    )
    if (audioError) {
      // Deduplicate audio validation toast for the same context.
      // Otherwise this effect can re-fire on toast-driven re-renders and loop.
      if (initAudioErrorContextRef.current !== currentContext) {
        initAudioErrorContextRef.current = currentContext
        alertToast({
          title: t(audioError.title),
          description: t(audioError.description, {
            model: getModelDisplayName(resolvedModelId, availableModels),
          }),
        })
      }
      return
    }

    initAudioErrorContextRef.current = null
    if (initSentContextRef.current === currentContext) return
    initSentContextRef.current = currentContext
    void sendMessage(currentContext.question)
  }, [
    askingStatus,
    currentContext,
    sendMessage,
    agent?.llmModel,
    userMe?.profile?.preferredLLM,
    alertToast,
    t,
    sysConfig,
    availableModels,
  ])

  const handleResolvePaymentInteraction = useCallback(() => {
    clearPendingInteraction()
    refreshConversations()
  }, [clearPendingInteraction, refreshConversations])

  // Feedback handlers for ActionBar
  const handleHelpfulFeedback = useCallback(
    async (part: ConversationPartApiResponse) => {
      const success = await submitFeedback(part.id, "sys_helpful")
      if (success) {
        successToast({
          title: t(
            "common.chat.feedback.thankYou",
            "Thank you for your feedback!"
          ),
        })
      } else {
        alertToast({
          title: t("common.chat.feedback.failed", "Failed to submit feedback"),
        })
      }
      return success
    },
    [alertToast, submitFeedback, successToast, t]
  )

  const handleImproveFeedback = useCallback(
    async (
      part: ConversationPartApiResponse,
      content?: string
    ): Promise<boolean> => {
      const success = await submitFeedback(part.id, "sys_improve", content)
      if (success) {
        successToast({
          title: t(
            "common.chat.feedback.thankYou",
            "Thank you for your feedback!"
          ),
        })
      } else {
        alertToast({
          title: t("common.chat.feedback.failed", "Failed to submit feedback"),
        })
      }
      return success
    },
    [alertToast, submitFeedback, successToast, t]
  )

  const handleFeedbackBlocked = useCallback(
    (reason: FeedbackBlockedReason) => {
      switch (reason) {
        case "helpfulAlreadySubmitted":
          alertToast({
            title: t(
              "common.chat.feedback.helpfulAlreadySubmitted",
              "You've already submitted helpful feedback for this message"
            ),
          })
          break
        case "improveAlreadySubmitted":
          alertToast({
            title: t(
              "common.chat.feedback.improveAlreadySubmitted",
              "You've already submitted improvement feedback for this message"
            ),
          })
          break
      }
    },
    [alertToast, t]
  )

  const handleSendQuestion = useCallback(async () => {
    const attachedFileUuids =
      uploadedFileUuids.length > 0 ? [...uploadedFileUuids] : undefined
    const attachedFiles = fileBadges.length > 0 ? [...fileBadges] : undefined

    if (!question.trim() && !attachedFileUuids?.length) return
    if (isUploading) return

    const resolvedModelId =
      currentContext?.settings?.agent?.llmModel ||
      agent?.llmModel ||
      userMe?.profile?.preferredLLM ||
      getSystemDefaultTextModelId(sysConfig, availableModels)
    const audioError = validateAudioModel(
      attachedFiles,
      resolvedModelId,
      availableModels
    )
    if (audioError) {
      alertToast({
        title: t(audioError.title),
        description: t(audioError.description, {
          model: getModelDisplayName(resolvedModelId, availableModels),
        }),
      })
      return
    }

    try {
      await sendMessage(question, {
        attachedFileUuids,
        attachedFiles,
        agentId: agent?.id,
        agentHistoryId,
        onUserPromptAccepted: () => {
          setQuestion("")
          clearFiles()
        },
        onError: () => {
          onConversationSettled?.()
        },
      })
    } catch (error) {
      console.error("Failed to send question:", error)
      alertToast({
        title: t("common.conversation.errors.startAskingFailed"),
        description: t("constants.errors.backToHome"),
      })
    }
  }, [
    question,
    isUploading,
    fileBadges,
    uploadedFileUuids,
    currentContext?.settings?.agent?.llmModel,
    agent?.llmModel,
    agent?.id,
    agentHistoryId,
    userMe?.profile?.preferredLLM,
    sendMessage,
    clearFiles,
    alertToast,
    t,
    sysConfig,
    availableModels,
    onConversationSettled,
  ])

  const handleCancelQuestion = useCallback(() => {
    console.debug("handleCancelQuestion")
    cancel()
  }, [cancel])

  const handleImageEditSubmit = useCallback(
    async (prompt: string, region: ImageEditRegion) => {
      if (
        !imageEditTarget ||
        isAsking ||
        isHitlPending ||
        isLoadingHistory ||
        isSubmittingImageEdit
      ) {
        return
      }
      setIsSubmittingImageEdit(true)
      try {
        await sendMessage(prompt, {
          imageEdit: {
            baseAttachmentId: imageEditTarget.attachmentId,
            region,
          },
          onUserPromptAccepted: () => setImageEditTarget(null),
        })
      } catch {
        // The shared hook reports the error; pre-acceptance failures leave the dialog open for retry.
      } finally {
        setIsSubmittingImageEdit(false)
      }
    },
    [
      imageEditTarget,
      isAsking,
      isHitlPending,
      isLoadingHistory,
      isSubmittingImageEdit,
      sendMessage,
    ]
  )

  const handlePaymentRequired = () => {
    setShowPaymentRequired(false)
    navigate(
      (billingUiEnabled ?? true)
        ? SETTINGS_ENDPOINTS.organizationPlan
        : SETTINGS_ENDPOINTS.organizationGeneral
    )
  }

  const isCopilotVariant = variant === "copilot"

  // Render function for group separator
  const renderGroupSeparator = useCallback(
    () => <Separator className="w-full mt-16 mb-4" />,
    []
  )

  // Content max-width class with container query for responsive width
  // Default: max-w-(--breakpoint-md), when container is large: max-w-2xl
  const contentMaxWidthClass =
    "mx-auto w-full max-w-(--breakpoint-md) @lg/playground:max-w-(--breakpoint-lg)"

  const renderCopilotIntroduction = () =>
    showCopilotIntroduction ? (
      <div
        className="mb-3 rounded-lg border border-primary/20 bg-primary/5 p-4"
        role="status"
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-md bg-primary/10 p-2 text-primary">
            <SparklesIcon className="h-4 w-4" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">
              {t("workspace.agentPlayground.firstRun.copilotTitle")}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("workspace.agentPlayground.firstRun.copilotDescription")}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={onOpenCopilot}>
                {t("workspace.agentPlayground.firstRun.openCopilot")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onDismissCopilotIntroduction}
              >
                {t("workspace.agentPlayground.firstRun.dismissCopilot")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    ) : null

  // Shared message content (part-native conversation part panel)
  const renderMessageContent = () => (
    <>
      {parts.length > 0 && (
        <WebConversationPartPanel
          className={contentMaxWidthClass}
          history={currentHistory ? toUIHistory(currentHistory) : undefined}
          parts={parts}
          isAsking={isAsking || isHitlPending}
          copilotFieldPrefix="liveTest.conversation_part"
          resolveUrl={resolveAttachmentUrl}
          // Feedback handlers
          onHelpfulFeedback={handleHelpfulFeedback}
          onImproveFeedback={handleImproveFeedback}
          onFeedbackBlocked={handleFeedbackBlocked}
          submittingFormRequestId={submittingFormRequestId}
          onSubmitFormInteraction={submitFormInteraction}
          onRejectFormInteraction={rejectFormInteraction}
          // Attachment handlers
          onDownload={handleAttachmentDownload}
          onPreviewLoad={handleAttachmentPreviewLoad}
          onEditImage={
            !supportsImageRegionEditing ||
            isAsking ||
            isHitlPending ||
            isLoadingHistory ||
            isSubmittingImageEdit
              ? undefined
              : setImageEditTarget
          }
          onRewritePart={(questionPart) =>
            regenerate(questionPart.conversation_group_id)
          }
          onEditPart={(_questionPart, content) => setQuestion(content)}
          onDeletePart={(questionPart) =>
            deleteConversationGroup(questionPart.conversation_group_id)
          }
          onCreateEvalPart={onCreateEvalPart}
          getHiddenActions={(_groupIndex, questionPart) => [
            ActionType.Delete,
            ...(!getAgentHistoryId(questionPart.metadata)
              ? [ActionType.Rewrite]
              : []),
          ]}
          getRewriteTooltip={(questionPart) => {
            const version = getAgentVersion(
              questionPart.metadata,
              agentHistories
            )
            return version == null
              ? t("workspace.agentPlayground.history.regenerateOriginalVersion")
              : t("workspace.agentPlayground.history.regenerateVersion", {
                  version,
                })
          }}
          renderAssistantMetadata={({ questionPart }) => {
            const version = getAgentVersion(
              questionPart.metadata,
              agentHistories
            )
            const label =
              version == null
                ? t("workspace.agentPlayground.history.versionUnknown")
                : `v${version}`
            const description =
              version == null
                ? getAgentHistoryId(questionPart.metadata)
                  ? t(
                      "workspace.agentPlayground.history.regenerateOriginalVersion"
                    )
                  : t(
                      "workspace.agentPlayground.history.versionUnknownDescription"
                    )
                : t("workspace.agentPlayground.history.versionDescription", {
                    version,
                  })

            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="mt-2 inline-flex cursor-default text-xs text-muted-foreground">
                    {label}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{description}</TooltipContent>
              </Tooltip>
            )
          }}
          // Slots
          renderGroupSeparator={renderGroupSeparator}
          renderPaymentSummary={(
            context,
            _message,
            paymentSummary,
            defaultNode
          ) => {
            if (
              pendingPayment?.interactionType !== "payment" ||
              pendingPayment.conversationGroupId !== context.groupId ||
              paymentSummary.requestNumber.trim() !==
                pendingPayment.payment.merchantOrderNo?.trim()
            ) {
              return defaultNode
            }

            return (
              <PendingInteractionPaymentCard
                pendingPayment={pendingPayment}
                onResolved={handleResolvePaymentInteraction}
              />
            )
          }}
          renderBetweenParts={(context, part) => {
            const pendingGroupId = pendingPayment?.conversationGroupId
            if (!pendingGroupId) return null

            const isTargetGroup = context.groupId === pendingGroupId
            const lastMessage = context.groupParts
              .filter(
                (item) =>
                  item.part_kind === "user-prompt" || item.part_kind === "text"
              )
              .at(-1)
            const isAfterLastMessage =
              lastMessage != null && lastMessage === part
            if (!isTargetGroup || !isAfterLastMessage) return null

            if (
              pendingPayment &&
              pendingPayment.interactionType === "payment"
            ) {
              const pendingPaymentRequestNumber =
                pendingPayment.payment.merchantOrderNo?.trim()
              const hasInlinePaymentSummary = context.groupParts.some(
                (item) => {
                  if (item.part_kind !== "text") return false
                  const content =
                    typeof item.content.content === "string"
                      ? item.content.content
                      : ""
                  const summary = parsePaymentSummaryContent(content)
                  return (
                    summary?.requestNumber.trim() ===
                    pendingPaymentRequestNumber
                  )
                }
              )
              if (hasInlinePaymentSummary) return null

              return (
                <PendingInteractionPaymentCard
                  pendingPayment={pendingPayment}
                  onResolved={handleResolvePaymentInteraction}
                />
              )
            }

            return null
          }}
        />
      )}

      {(isAsking || isHitlPending) && currentContext && parts.length === 0 && (
        <Separator className="w-full mt-16 mb-4" />
      )}

      {showFirstTestGuidance &&
        parts.length === 0 &&
        !isAsking &&
        !isHitlPending && (
          <OnboardingEmptyState
            className="min-h-[18rem]"
            icon={MessageSquareIcon}
            title={t("workspace.agentPlayground.firstRun.liveTestTitle")}
            description={t(
              "workspace.agentPlayground.firstRun.liveTestDescription"
            )}
          />
        )}

      {showCopilotIntroduction && parts.length > 0 && (
        <div className={`${contentMaxWidthClass} pb-4`}>
          {renderCopilotIntroduction()}
        </div>
      )}
    </>
  )

  const renderVersionTestGuidance = () =>
    showVersionTestGuidance ? (
      <div
        className="mb-3 rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-4"
        role="status"
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-md bg-emerald-500/10 p-2 text-emerald-700 dark:text-emerald-300">
            <Repeat2Icon className="h-4 w-4" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">
              {t("workspace.agentPlayground.versionTest.title", {
                version: versionTestVersion,
              })}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("workspace.agentPlayground.versionTest.description")}
            </p>
            <Button
              className="mt-3"
              size="sm"
              onClick={onRetryPreviousQuestion}
            >
              {versionTestQuestion
                ? t("workspace.agentPlayground.versionTest.retry", {
                    version: versionTestVersion,
                  })
                : t("workspace.agentPlayground.versionTest.start")}
            </Button>
          </div>
        </div>
      </div>
    ) : null

  // Shared question input panel
  const renderQuestionInput = () => (
    <div className="border rounded-md p-4 bg-background">
      <QuestionPanel
        message={question}
        isAsking={isAsking}
        minHeight={70}
        placeholder={
          showFirstTestGuidance
            ? t("workspace.agentPlayground.firstRun.liveTestPlaceholder")
            : undefined
        }
        fileBadges={fileBadges}
        isUploading={isUploading}
        enableVoiceRecording={!voiceRecordingDisabledReason}
        voiceRecordingDisabledReason={voiceRecordingDisabledReason}
        resolveUrl={resolveAttachmentUrl}
        onPreviewLoad={handleAttachmentPreviewLoad}
        onMessageChange={setQuestion}
        onSend={handleSendQuestion}
        onCancel={handleCancelQuestion}
        onFilesSelected={handleFileUpload}
        onFileRemove={removeFile}
        onDownload={handleAttachmentDownload}
        onVoiceRecordingError={handleVoiceRecordingError}
      />
    </div>
  )

  const imageEditDialog = (
    <ImageEditDialog
      open={imageEditTarget !== null}
      target={imageEditTarget ?? undefined}
      submitting={isSubmittingImageEdit}
      onOpenChange={(open) => {
        if (!open) setImageEditTarget(null)
      }}
      onSubmit={handleImageEditSubmit}
    />
  )

  const persistedHistory =
    currentHistory && currentHistory.id > 0 ? currentHistory : undefined
  const copilotContextFields = (
    <>
      {agent && (
        <CopilotHiddenField
          fieldKey="liveTest.agentId"
          value={agent.id.toString()}
        />
      )}
      {persistedHistory && (
        <>
          <CopilotHiddenField
            fieldKey="liveTest.history.selectedId"
            value={persistedHistory.id.toString()}
          />
          <CopilotHiddenField
            fieldKey="liveTest.history.title"
            value={persistedHistory.name}
          />
        </>
      )}
    </>
  )

  // Copilot variant: original layout with fixed/absolute positioning
  if (isCopilotVariant) {
    return (
      <div
        ref={scrollContainerRef}
        data-scroll="playground"
        className="relative px-2 overflow-y-auto flex h-full min-h-0 flex-1 flex-col pb-40"
      >
        {copilotContextFields}
        <div className="flex flex-col gap-4 pb-32">
          {renderMessageContent()}
        </div>

        <ScrollToBottomButton
          className="z-20"
          show={showScrollToBottom}
          onClick={scrollToBottom}
          position="absolute"
          bottom="100px"
        />

        <div className="pointer-events-none z-10 absolute bottom-4 left-0 right-0 px-6">
          <div className="h-full w-full pointer-events-auto">
            <div className={contentMaxWidthClass}>
              {renderVersionTestGuidance()}
              {renderQuestionInput()}
            </div>
          </div>
        </div>
        {imageEditDialog}
      </div>
    )
  }

  // Default variant: Two-section flexbox layout
  // Top: scrollable message area (flex-1)
  // Bottom: fixed input area (shrink-0)
  return (
    <div className="flex flex-col h-full">
      {copilotContextFields}
      {/* Scrollable message area */}
      <div className="flex-1 relative overflow-hidden">
        <div
          ref={scrollContainerRef}
          data-scroll="playground"
          className="absolute inset-0 overflow-y-auto px-2"
        >
          <div className="flex flex-col gap-4 py-0">
            {renderMessageContent()}
          </div>
        </div>

        <ScrollToBottomButton
          className="z-20"
          show={showScrollToBottom}
          onClick={scrollToBottom}
          position="absolute"
          bottom="20px"
        />
      </div>

      {/* Fixed input area at bottom */}
      <div className="shrink-0 p-4">
        <div className={contentMaxWidthClass}>
          {renderVersionTestGuidance()}
          {renderQuestionInput()}
        </div>
      </div>
      <PaymentRequiredDialog
        open={showPaymentRequired}
        onOpenChange={setShowPaymentRequired}
        onConfirm={handlePaymentRequired}
      />
      {imageEditDialog}
    </div>
  )
}

const ThreadPanel = forwardRef<ThreadPanelHandle, ThreadPanelProps>(
  ThreadPanelComponent
)

export default ThreadPanel
